import { useCallback, useEffect, useRef, useState } from 'react'

// The rows of a sub-items list whose own round trip is out. A row reads a slice of the question's
// answer, so the question's loader never covers a fetch made for one row alone, and without this
// the row shows its old answer -- or an empty one -- until the new one lands.
//
// A row leaves when the shell reports its own fetch done (`onRowsLoaded`, named by the key the
// row's params hang off, `<question key>.<position>`), so two rows asked for at once settle apart.
// A row fetch can also end in a reload of the whole question that never reports the row back, so
// fresh answers let every waiting row go as well.
export default function useLoadingRows({ questionKey, answer }) {
  const [loadingRows, setLoadingRows] = useState(() => new Set())

  const markRows = useCallback((positions, on) => {
    setLoadingRows(current => {
      const next = new Set(current)
      for (const position of positions) {
        if (on) next.add(String(position))
        else next.delete(String(position))
      }
      return next.size === current.size && [...next].every(position => current.has(position)) ? current : next
    })
  }, [])

  const markRowLoading = useCallback(position => markRows([position], true), [markRows])

  const keyRef = useRef(questionKey)
  keyRef.current = questionKey

  const onRowsLoaded = useCallback(
    keys => {
      const prefix = `${keyRef.current}.`
      markRows(
        (keys || []).map(key => (String(key).startsWith(prefix) ? String(key).slice(prefix.length) : String(key))),
        false
      )
    },
    [markRows]
  )

  const lastAnswer = useRef(answer)
  useEffect(() => {
    if (lastAnswer.current === answer) return
    lastAnswer.current = answer

    if ('loading' !== answer) setLoadingRows(current => (current.size ? new Set() : current))
  }, [answer])

  return { loadingRows, markRowLoading, onRowsLoaded }
}
